import { Observable, type ServerError } from "@apollo/client";
import { type ErrorResponse, onError } from "@apollo/client/link/error";
import { addTypenameToDocument, print } from "@apollo/client/utilities";
import type { GraphQLFormattedError } from "graphql";
import { clearRoomAccessDetails } from "@/features/ird-qr/utils";
import { REFRESH_TOKEN } from "@/graphql/public/auth";
import { useAnalyticsContext } from "@/lib/analytics/context";
import {
  AUTH_ERROR_CODE,
  isSessionRequiredRoute,
} from "@/lib/apollo/authErrors";
import { sha256 } from "@/lib/apollo/persistedQueries";
import { useAuthStore } from "@/stores/authStore";
import { useFlowStore } from "@/stores/flowStore";

interface Observer {
  error: (err: unknown) => void;
}

// The refresh runs when the access token cookie has lapsed, so the BFF treats
// it as anonymous and only accepts it by its persisted-operation hash. The
// hash must match the manifest, which is generated from the typename-added
// document.
const REFRESH_QUERY = print(addTypenameToDocument(REFRESH_TOKEN));

const createRefreshRequestBody = async () =>
  JSON.stringify({
    operationName: "RefreshToken",
    query: REFRESH_QUERY,
    extensions: {
      persistedQuery: { version: 1, sha256Hash: await sha256(REFRESH_QUERY) },
    },
  });

const MAX_REFRESH_FAILURES = 1;

interface RefreshState {
  isRefreshing: boolean;
  pendingObservers: Array<{ callback: () => void; observer: Observer }>;
  refreshFailureCount: number;
}

function createRefreshState(): RefreshState {
  return {
    isRefreshing: false,
    pendingObservers: [],
    refreshFailureCount: 0,
  };
}

function resolvePending(state: RefreshState) {
  state.pendingObservers.forEach(({ callback }) => {
    callback();
  });
  state.pendingObservers = [];
}

function rejectPending(state: RefreshState, err: unknown) {
  state.pendingObservers.forEach(({ observer }) => {
    observer.error(err);
  });
  state.pendingObservers = [];
}

const isOnSessionRequiredRoute = () =>
  typeof window !== "undefined" &&
  isSessionRequiredRoute(window.location.pathname);

const redirectToLogin = () => {
  const { hotelId, clearAll: clearFlowStore } = useFlowStore.getState();
  clearFlowStore();
  window.location.href = hotelId ? `/login?hotel_id=${hotelId}` : "/login";
};

const handleSessionLost = (
  state: RefreshState,
  err: unknown,
  observer?: Observer,
) => {
  rejectPending(state, err);
  if (typeof window === "undefined") return;

  useAuthStore.getState().clearAll();
  useAnalyticsContext.getState().clearContext();
  clearRoomAccessDetails();

  if (isOnSessionRequiredRoute()) {
    redirectToLogin();
    return;
  }
  observer?.error(err);
};

const getResponseErrors = ({
  response,
  networkError,
}: Pick<ErrorResponse, "response" | "networkError">):
  | readonly GraphQLFormattedError[]
  | undefined => {
  if (response?.errors) return response.errors;
  const result = (networkError as ServerError | null | undefined)?.result;
  return typeof result === "object" ? result?.errors : undefined;
};

const findErrorWithCode = (
  errors: readonly GraphQLFormattedError[],
  code: string,
) => errors.find((error) => error.extensions?.code === code);

const resetRefreshFailuresOnLogin = (state: RefreshState) => {
  if (typeof window === "undefined") return;

  useAuthStore.subscribe((authState, previousAuthState) => {
    if (authState.userId && !previousAuthState.userId) {
      state.refreshFailureCount = 0;
    }
  });
};

export const createErrorLink = (graphqlUrl: string) => {
  const state = createRefreshState();
  resetRefreshFailuresOnLogin(state);

  return onError(({ response, networkError, operation, forward }) => {
    const errors = getResponseErrors({ response, networkError });
    if (!errors) return;

    const unauthenticatedError = findErrorWithCode(
      errors,
      AUTH_ERROR_CODE.UNAUTHENTICATED,
    );
    if (!unauthenticatedError) return;

    if (state.refreshFailureCount >= MAX_REFRESH_FAILURES) {
      handleSessionLost(state, unauthenticatedError);
      return;
    }

    if (state.isRefreshing) {
      return new Observable((observer) => {
        state.pendingObservers.push({
          callback: () => forward(operation).subscribe(observer),
          observer,
        });
      });
    }

    state.isRefreshing = true;

    return new Observable((observer) => {
      createRefreshRequestBody()
        .then((body) =>
          fetch(graphqlUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Application-Id": "guest-connect-frontend",
            },
            credentials: "include",
            body,
          }),
        )
        .then((res) => res.json())
        .then((result) => {
          const refreshResult = result.data?.refreshToken;
          state.isRefreshing = false;

          if (refreshResult?.success) {
            state.refreshFailureCount = 0;
            resolvePending(state);
            forward(operation).subscribe(observer);
          } else {
            state.refreshFailureCount++;
            handleSessionLost(state, unauthenticatedError, observer);
          }
        })
        .catch(() => {
          state.isRefreshing = false;
          state.refreshFailureCount++;
          handleSessionLost(state, unauthenticatedError, observer);
        });
    });
  });
};
